import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaTimes, FaPenAlt } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
const { rootUrl } = require('../../../config/config');

const InspectionCheckItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    item_name: '',
    item_code: '',
    description: '',
    category: '',
    display_order: ''
  });
  const { user } = useAuth();

  // Fetch all inspection check items
  const fetchItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || 'default-token';
      const response = await axios.get(`${rootUrl}/cars/inspections/check-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch inspection check items.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Start editing a row - populate form with item data
  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      item_name: item.item_name || '',
      item_code: item.item_code || '',
      description: item.description || '',
      category: item.category || '',
      display_order: item.display_order || ''
    });
    // Scroll to top to show the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cancel editing/adding - clear form
  const cancelForm = () => {
    setEditingId(null);
    setForm({
      item_name: '',
      item_code: '',
      description: '',
      category: '',
      display_order: ''
    });
    setError('');
  };

  // Delete an item
  const deleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inspection check item?')) return;
    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      await axios.delete(`${rootUrl}/cars/inspections/check-items/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchItems();
    } catch (err) {
      setError('Failed to delete inspection check item.');
      console.error(err);
    }
  };

  // Handle form submission for both add and edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.item_name.trim() || !form.item_code.trim()) {
      setError('Item name and item code are required.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      
      if (editingId) {
        // Update existing item
        await axios.put(
          `${rootUrl}/cars/inspections/check-items/${editingId}`,
          {
            ...form,
            edit_by: user?.username || 'system'
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // Add new item
        await axios.post(
          `${rootUrl}/cars/inspections/check-items`,
          {
            ...form,
            input_by: user?.username || 'system'
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      
      cancelForm();
      fetchItems();
    } catch (err) {
      setError(editingId 
        ? 'Failed to update inspection check item.' 
        : 'Failed to add inspection check item.');
      console.error(err);
    }
  };

  return (
    <>
      <Card className="w-full max-w-6xl mt-6 border-t">
        <CardBody>
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            Inspection Check Items
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Add/Edit Form */}
          <form onSubmit={handleSubmit} className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">
                {editingId ? 'Edit Check Item' : 'Add New Check Item'}
              </h3>
              {editingId && (
                <Button
                  type="button"
                  variant="outlined"
                  color="gray"
                  onClick={cancelForm}
                  className="flex items-center"
                >
                  <FaTimes className="mr-2" /> Cancel
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                label="Item Name *"
                value={form.item_name}
                onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                required
              />
              <Input
                type="text"
                label="Item Code *"
                value={form.item_code}
                onChange={(e) => setForm({ ...form, item_code: e.target.value.toUpperCase() })}
                className="uppercase"
                style={{ textTransform: 'uppercase' }}
                required
              />
              <Input
                type="text"
                label="Category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
              <Input
                type="number"
                label="Display Order"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
              />
            </div>
            <Input
              type="text"
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mb-4"
            />
            <Button
              type="submit"
              variant="gradient"
              color="blue"
              className="flex items-center"
            >
              <FaPlus className="mr-2" /> {editingId ? 'Update Check Item' : 'Add Check Item'}
            </Button>
          </form>

          {/* Items Table */}
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">ID</th>
                    <th className="border px-4 py-2 text-left">Item Name</th>
                    <th className="border px-4 py-2 text-left">Item Code</th>
                    <th className="border px-4 py-2 text-left">Category</th>
                    <th className="border px-4 py-2 text-left">Description</th>
                    <th className="border px-4 py-2 text-left">Order</th>
                    <th className="border px-4 py-2 text-center">Status</th>
                    <th className="border px-4 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="border px-4 py-2">{item.id}</td>
                      <td className="border px-4 py-2">
                        <Typography>{item.item_name}</Typography>
                      </td>
                      <td className="border px-4 py-2">
                        <Typography className="uppercase">{item.item_code}</Typography>
                      </td>
                      <td className="border px-4 py-2">
                        <Typography>{item.category || '-'}</Typography>
                      </td>
                      <td className="border px-4 py-2">
                        <Typography className="text-sm">{item.description || '-'}</Typography>
                      </td>
                      <td className="border px-4 py-2">
                        <Typography>{item.display_order || '-'}</Typography>
                      </td>
                      <td className="border px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="border px-4 py-2 text-center space-y-2 sm:space-y-0 sm:space-x-2">
                        <Button
                          onClick={() => startEdit(item)}
                          className="bg-transparent shadow-none"
                          disabled={editingId === item.id}
                        >
                          <FaPenAlt className="text-blue-gray-700" />
                        </Button>
                        <Button
                          onClick={() => deleteItem(item.id)}
                          className="bg-transparent shadow-none"
                          disabled={editingId === item.id}
                        >
                          <FaTrash className="text-red-700" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default InspectionCheckItems;

